(function () {
    const { filters, devicePixelRatio } = fabric.Image;
    const { createClass, array, createCanvasElement } = fabric.util;

    filters.Perspective = createClass(filters.BaseFilter, {
        type: 'Perspective',
        step: 2,
        pixelRatio: devicePixelRatio || 1,
        bounds: { width: 0, height: 0, minX: 0, maxX: 0, minY: 0, maxY: 0 },
        debug: false,

        initialize(options) {
            this.callSuper('initialize', options);
            this.applyPixelRatio();
        },

        updateCoords(coords) {
            this.perspectiveCoords = coords;
            this.applyPixelRatio();
        },

        applyPixelRatio(coords = this.perspectiveCoords) {
            coords.forEach((coord) => {
                coord[0] *= this.pixelRatio;
                coord[1] *= this.pixelRatio;
            });
            return coords;
        },

        getPerspectiveBounds(coords = this.perspectiveCoords) {
            const mappedCoords = coords.slice().map((c) => ({ x: c[0], y: c[1] }));
            const { min, max } = array;

            this.bounds.minX = min(mappedCoords, 'x') || 0;
            this.bounds.minY = min(mappedCoords, 'y') || 0;
            this.bounds.maxX = max(mappedCoords, 'x') || 0;
            this.bounds.maxY = max(mappedCoords, 'y') || 0;

            this.bounds.width = Math.abs(this.bounds.maxX - this.bounds.minX);
            this.bounds.height = Math.abs(this.bounds.maxY - this.bounds.minY);

            return { ...this.bounds };
        },

        calculateCoordsByCorners(coords = this.perspectiveCoords) {
            coords.forEach((coord) => {
                coord[0] -= this.bounds.minX + this.bounds.width / 2;
                coord[1] -= this.bounds.minY + this.bounds.height / 2;
            });
        },

        _centerToAbsolute(coords, width, height) {
            coords.forEach((coord) => {
                coord[0] += width / 2;
                coord[1] += height / 2;
            });
        },

        applyTo2d(options) {
            const { imageData } = options;
            const { width, height } = imageData;

            const sourceCanvas = createCanvasElement();
            sourceCanvas.width = width;
            sourceCanvas.height = height;
            const sourceCtx = sourceCanvas.getContext('2d');
            sourceCtx.putImageData(imageData, 0, 0);

            const tempCanvas = createCanvasElement();
            tempCanvas.width = width;
            tempCanvas.height = height;
            const tempCtx = tempCanvas.getContext('2d');

            const destCanvas = createCanvasElement();
            destCanvas.width = width;
            destCanvas.height = height;
            if (this.debug) {
                this._debugCanvas(destCanvas);
            }
            const destCtx = destCanvas.getContext('2d');
            const percentageCoords = this.perspectiveCoords.map((coord) => [
                (coord[0] / 100) * width,
                (coord[1] / 100) * height
            ]);
            this._centerToAbsolute(percentageCoords, width, height);

            const points = percentageCoords;
            const dims = this._calculateDimensions(points);
            const baseIndex = this._findLongestSide(dims);

            const coverStep = this.step * 5;

            tempCtx.clearRect(0, 0, width, height);

            if (baseIndex % 2 === 0) {
                this._applyHorizontalStrips(sourceCanvas, tempCtx, points, width, height, this.step, coverStep);
            } else {
                this._applyVerticalStrips(sourceCanvas, tempCtx, points, width, height, this.step, coverStep);
            }

            destCtx.save();
            this._applyClipPath(destCtx, points);
            destCtx.drawImage(tempCanvas, 0, 0);
            destCtx.restore();

            options.imageData = destCtx.getImageData(0, 0, width, height);
        },

        _applyClipPath(ctx, points) {
            ctx.beginPath();
            ctx.moveTo(points[0][0], points[0][1]);
            ctx.lineTo(points[1][0], points[1][1]);
            ctx.lineTo(points[2][0], points[2][1]);
            ctx.lineTo(points[3][0], points[3][1]);
            ctx.closePath();
            ctx.clip();
        },

        _debugCanvas(destCanvas) {
            destCanvas.id = 'destCanvas';
            destCanvas.style.position = 'absolute';
            destCanvas.style.transform = 'translate(0px, -200px) scale(0.5)';
            destCanvas.style.border = '1px solid red';
            const existingCanvas = document.getElementById('destCanvas');
            if (existingCanvas) {
                existingCanvas.remove();
            }
            document.body.append(destCanvas);
        },

        _calculateDimensions(points) {
            return [
                Math.sqrt(Math.pow(points[0][0] - points[1][0], 2) + Math.pow(points[0][1] - points[1][1], 2)),
                Math.sqrt(Math.pow(points[1][0] - points[2][0], 2) + Math.pow(points[1][1] - points[2][1], 2)),
                Math.sqrt(Math.pow(points[2][0] - points[3][0], 2) + Math.pow(points[2][1] - points[3][1], 2)),
                Math.sqrt(Math.pow(points[3][0] - points[0][0], 2) + Math.pow(points[3][1] - points[0][1], 2))
            ];
        },

        _findLongestSide(dims) {
            return dims.reduce((maxIndex, dim, i) => (dim > dims[maxIndex] ? i : maxIndex), 0);
        },

        _applyHorizontalStrips(sourceCanvas, tempCtx, points, width, height, step, coverStep) {
            const stripCanvas = createCanvasElement();
            stripCanvas.width = width;
            stripCanvas.height = coverStep;
            const stripCtx = stripCanvas.getContext('2d');
            stripCtx.globalCompositeOperation = 'copy';

            for (let y = 0; y < height; y += step) {
                const r = y / height;
                const [sx, sy] = this._interpolate(points[0], points[3], r);
                const [ex, ey] = this._interpolate(points[1], points[2], r);

                const angle = Math.atan((ey - sy) / (ex - sx));
                const scale = Math.sqrt(Math.pow(ex - sx, 2) + Math.pow(ey - sy, 2)) / width;

                stripCtx.setTransform(1, 0, 0, 1, 0, -y);
                stripCtx.drawImage(sourceCanvas, 0, 0);

                tempCtx.translate(sx, sy);
                tempCtx.rotate(angle);
                tempCtx.scale(scale, scale);
                tempCtx.drawImage(stripCanvas, 0, 0);
                tempCtx.setTransform(1, 0, 0, 1, 0, 0);
            }
        },

        _applyVerticalStrips(sourceCanvas, tempCtx, points, width, height, step, coverStep) {
            const stripCanvas = createCanvasElement();
            stripCanvas.width = coverStep;
            stripCanvas.height = height;
            const stripCtx = stripCanvas.getContext('2d');
            stripCtx.globalCompositeOperation = 'copy';

            for (let x = 0; x < width; x += step) {
                const r = x / width;
                const [sx, sy] = this._interpolate(points[0], points[1], r);
                const [ex, ey] = this._interpolate(points[3], points[2], r);

                const angle = Math.atan((sx - ex) / (ey - sy));
                const scale = Math.sqrt(Math.pow(ex - sx, 2) + Math.pow(ey - sy, 2)) / height;

                stripCtx.setTransform(1, 0, 0, 1, -x, 0);
                stripCtx.drawImage(sourceCanvas, 0, 0);

                tempCtx.translate(sx, sy);
                tempCtx.rotate(angle);
                tempCtx.scale(scale, scale);
                tempCtx.drawImage(stripCanvas, 0, 0);
                tempCtx.setTransform(1, 0, 0, 1, 0, 0);
            }
        },

        _interpolate([x1, y1], [x2, y2], r) {
            return [x1 + (x2 - x1) * r, y1 + (y2 - y1) * r];
        }
    });

    /**
     * Returns filter instance from an object representation
     * @static
     * @param {Object} object Object to create an instance from
     * @param {Function} [callback] to be invoked after filter creation
     * @return {fabric.Image.filters.Perspective} Instance of fabric.Image.filters.Perspective
     */
    fabric.Image.filters.Perspective.fromObject = fabric.Image.filters.BaseFilter.fromObject;
})();
